import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { loadAdSenseOnDemand, isAdSenseReady } from '../utils/optimizedThirdPartyLoader';

/**
 * Enhanced Mobile AdSense Banner - Content-Aware Ad Pushing
 * Provides Google AdSense with current content context for better ad targeting
 */

// ✅ ENHANCED: Content information interface for AdSense with expanded context
interface ContentInfo {
  url: string;
  title: string;
  excerpt?: string;
  category?: string;
  tags?: string[];
  language?: string;
  // ✅ NEW: Additional context for better ad targeting
  publishDate?: string;
  readingTime?: number;
  engagement?: 'low' | 'medium' | 'high';
  postType?: 'article' | 'video' | 'audio' | 'gallery';
  topicCategory?: string;
}

// ✅ GLOBAL STATE: Enhanced tracking with content awareness
const globalInitializedPosts = new Set<number>();
const adTrackingState = {
  lastAdTime: 0,
  lastAdPostCount: 0,
  failedAttempts: 0
};
let postViewCount = 0;
let lastReloadCount = 0;

// Enhanced adsbygoogle declaration
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

const AdContainer = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: transparent; /* ✅ TRANSPARENT: Show background underneath */
  /* ✅ ALWAYS VISIBLE: No transform, always fixed at top */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2.5px 5px;
  height: 55px; /* ✅ EXACT HEIGHT: 55px for 320x50 AdSense */
  
  /* Safe area support for iOS devices */
  padding-top: max(2.5px, env(safe-area-inset-top, 2.5px));
  
  @media (max-width: 768px) {
    padding: 2.5px 3px;
    height: 55px;
  }
`;

const AdWrapper = styled.div`
  width: 320px;
  height: 50px; /* ✅ STANDARD ADSENSE SIZE: 320x50 */
  display: flex;
  justify-content: center;
  align-items: center;
  
  .adsbygoogle {
    display: inline-block !important;
    width: 320px !important;
    height: 50px !important; /* ✅ STANDARD HEIGHT */
  }
  
  @media (max-width: 320px) {
    width: 100%;
    
    .adsbygoogle {
      width: 100% !important;
    }
  }
`;

// Loading placeholder when ad is not ready
const AdPlaceholder = styled.div`
  width: 320px;
  height: 50px; /* ✅ MATCH ADSENSE SIZE */
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent; /* ✅ TRANSPARENT: No loading background */
  border-radius: 4px;
  color: transparent; /* ✅ HIDE TEXT: Make text invisible */
  font-size: 12px;
  
  @media (max-width: 320px) {
    width: 100%;
  }
  
  /* ✅ REMOVED: No loading text displayed */
  &::after {
    content: ''; /* ✅ EMPTY: No "Đang tải quảng cáo..." text */
  }
`;

interface MobileTopAdSenseBannerProps {
  isPostVisible: boolean;
  postId?: number;
  // ✅ NEW: Content information for better ad targeting
  contentInfo?: ContentInfo;
}

// ✅ RELOAD FUNCTION: Refresh AdSense script every 5 posts
const shouldReloadAdSense = (): boolean => {
  postViewCount++;
  const shouldReload = postViewCount % 5 === 0 && postViewCount > lastReloadCount;
  if (shouldReload) {
    lastReloadCount = postViewCount;
    console.log(`[MobileTopAdSenseBanner] 🔄 Triggering AdSense reload after ${postViewCount} posts`);
  }
  return shouldReload;
};

const reloadAdSenseScript = async (): Promise<void> => {
  try {
    console.log('[MobileTopAdSenseBanner] 🔄 Reloading AdSense script...');
    
    // Remove existing script
    const existingScript = document.querySelector('script[src*="googlesyndication.com"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    // Clear adsbygoogle array
    if (window.adsbygoogle) {
      window.adsbygoogle.length = 0;
    }
    
    // Reload script
    await loadAdSenseOnDemand();
    
    console.log('[MobileTopAdSenseBanner] ✅ AdSense script reloaded successfully');
  } catch (error) {
    console.error('[MobileTopAdSenseBanner] ❌ Error reloading AdSense script:', error);
  }
};

// ✅ ENHANCED: Content-aware ad pushing function with intelligent targeting
const pushContentAwareAd = async (contentInfo: ContentInfo): Promise<boolean> => {
  try {
    if (!window.adsbygoogle || !Array.isArray(window.adsbygoogle)) {
      console.error('[MobileTopAdSenseBanner] ❌ AdSense not ready for content-aware push');
      return false;
    }

    // ✅ ENHANCED: Push ad with rich content context and improved targeting
    const adConfig = {
      google_ad_client: "ca-pub-8980858398695883",
      google_ad_slot: "4979920352",
      // ✅ ENHANCED: Content context for better targeting
      page_url: contentInfo.url,
      page_title: contentInfo.title,
      content_category: contentInfo.category || 'news',
      content_language: contentInfo.language || 'vi',
      content_keywords: contentInfo.tags?.slice(0, 5).join(',') || '', // Max 5 tags
      content_description: contentInfo.excerpt?.substring(0, 150) || '',
      // ✅ ENHANCED: Advanced targeting hints
      targeting_hints: {
        content_type: 'article',
        page_type: 'mobile_feed',
        user_interest: contentInfo.tags?.slice(0, 3) || [],
        content_freshness: 'recent',
        content_engagement: 'high',
        platform: 'mobile'
      },
      // ✅ NEW: AdSense optimization parameters
      optimization_params: {
        refresh_rate: 'medium',
        viewability_threshold: 0.5,
        content_aware: true,
        mobile_optimized: true
      }
    };

    console.log(`[MobileTopAdSenseBanner] 🎯 Pushing enhanced content-aware ad:`, {
      url: contentInfo.url,
      title: contentInfo.title.substring(0, 50) + '...',
      category: contentInfo.category,
      tags: contentInfo.tags?.slice(0, 3),
      configKeys: Object.keys(adConfig).length,
      optimizationEnabled: true
    });

    // ✅ ENHANCED: Instead of reloading script, push new ad with refreshed context
    window.adsbygoogle.push(adConfig);
    
    // ✅ NEW: Ensure Google AdSense knows about the new content immediately
    if (typeof window.googletag !== 'undefined' && window.googletag.pubads) {
      window.googletag.pubads().refresh();
      console.log('[MobileTopAdSenseBanner] 🔄 Triggered Google Ad Manager refresh');
    }
    
    // Track successful content-aware push
    adTrackingState.lastAdTime = Date.now();
    adTrackingState.lastAdPostCount = postViewCount;
    adTrackingState.failedAttempts = 0;
    
    return true;
  } catch (error) {
    console.error('[MobileTopAdSenseBanner] ❌ Error pushing enhanced content-aware ad:', error);
    adTrackingState.failedAttempts = (adTrackingState.failedAttempts || 0) + 1;
    return false;
  }
};

// ✅ FALLBACK: Standard ad push without content context
const pushStandardAd = async (contentInfo?: ContentInfo): Promise<boolean> => {
  try {
    if (!window.adsbygoogle) {
      console.error('[MobileTopAdSenseBanner] ❌ AdSense not ready for standard push');
      return false;
    }

    console.log('[MobileTopAdSenseBanner] 🔄 Pushing standard ad (fallback mode)');
    
    // Standard AdSense push
    window.adsbygoogle.push({});
    
    if (contentInfo) {
      // ✅ ANALYTICS: Track content info even with standard ads
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'ad_content_fallback', {
          page_url: contentInfo.url,
          page_title: contentInfo.title.substring(0, 100),
          content_category: contentInfo.category || 'news'
        });
      }
    }
    
    return true;
  } catch (error) {
    console.error('[MobileTopAdSenseBanner] ❌ Error pushing standard ad:', error);
    return false;
  }
};

const MobileTopAdSenseBanner: React.FC<MobileTopAdSenseBannerProps> = ({ 
  isPostVisible, 
  postId,
  contentInfo
}) => {
  const adRef = useRef<HTMLModElement>(null);
  const visibilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [shouldLoadAd, setShouldLoadAd] = useState(false);
  const [adReady, setAdReady] = useState(false);
  const [reloading, setReloading] = useState(false);

  // 🎯 PERFORMANCE OPTIMIZATION: Only load ad when post is visible for 500ms
  useEffect(() => {
    if (isPostVisible && postId) {
      console.log(`[MobileTopAdSenseBanner] 👁️ Post ${postId} visible, starting load timer...`);
      
      // Clear any existing timeout
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
      
      // Wait 500ms before loading ad to avoid loading for quick swipes
      visibilityTimeoutRef.current = setTimeout(() => {
        if (isPostVisible) { // Double-check still visible
          console.log(`[MobileTopAdSenseBanner] 🎯 Loading AdSense for post ${postId} after visibility delay`);
          setShouldLoadAd(true);
        }
      }, 500);
      
    } else {
      // Clear timeout if post becomes invisible
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
        visibilityTimeoutRef.current = null;
      }
    }
    
    return () => {
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
        visibilityTimeoutRef.current = null;
      }
    };
  }, [isPostVisible, postId]);

  // 🚀 SMART LOADING: Load AdSense with reload logic every 5 posts
  useEffect(() => {
    const loadAndInitializeAd = async () => {
      if (shouldLoadAd && postId && !globalInitializedPosts.has(postId) && adRef.current) {
        try {
          console.log(`[MobileTopAdSenseBanner] 🚀 Loading AdSense for post ${postId}... (Total initialized: ${globalInitializedPosts.size})`);
          
          // ✅ CHECK FOR RELOAD: Every 5 posts
          if (shouldReloadAdSense()) {
            setReloading(true);
            await reloadAdSenseScript();
            setReloading(false);
          }
          
          // Ensure AdSense script is loaded
          const adSenseLoaded = await loadAdSenseOnDemand();
          
          if (!adSenseLoaded) {
            console.error(`[MobileTopAdSenseBanner] ❌ AdSense script failed to load for post ${postId}`);
            setAdReady(false);
            return;
          }

          // Double-check AdSense is ready
          if (!isAdSenseReady() || !window.adsbygoogle) {
            console.warn(`[MobileTopAdSenseBanner] ⚠️ AdSense script loaded but not ready for post ${postId}, waiting...`);
            
            // Wait a bit more for AdSense to be fully ready
            await new Promise(resolve => setTimeout(resolve, 500));
            
            if (!window.adsbygoogle) {
              console.error(`[MobileTopAdSenseBanner] ❌ AdSense still not ready after waiting for post ${postId}`);
              setAdReady(false);
              return;
            }
          }

          console.log(`[MobileTopAdSenseBanner] 🎯 Initializing AdSense banner for post ${postId} (adsbygoogle array length: ${window.adsbygoogle.length})`);
          
          // ✅ ENHANCED: Use content-aware ad pushing when content info is available
          if (contentInfo) {
            const success = await pushContentAwareAd(contentInfo);
            if (!success) {
              console.warn(`[MobileTopAdSenseBanner] ⚠️ Content-aware ad failed, falling back to standard ad for post ${postId}`);
              await pushStandardAd(contentInfo);
            }
          } else {
            // ✅ FALLBACK: Standard ad push when no content info
            await pushStandardAd();
          }
          
          globalInitializedPosts.add(postId);
          setAdReady(true);
          
          console.log(`[MobileTopAdSenseBanner] ✅ AdSense banner initialized successfully for post ${postId} (new array length: ${window.adsbygoogle.length})`);
          
        } catch (error) {
          console.error(`[MobileTopAdSenseBanner] ❌ Error loading/initializing AdSense for post ${postId}:`, error);
          // ✅ ENHANCED: Try fallback ad on error
          if (contentInfo) {
            await pushStandardAd(contentInfo);
          }
          setAdReady(false);
          setReloading(false);
        }
      } else if (shouldLoadAd && postId && globalInitializedPosts.has(postId)) {
        // Post already initialized, just mark as ready
        console.log(`[MobileTopAdSenseBanner] ℹ️ Post ${postId} already initialized, marking as ready`);
        setAdReady(true);
      } else if (shouldLoadAd && !postId) {
        console.warn(`[MobileTopAdSenseBanner] ⚠️ shouldLoadAd is true but no postId provided`);
      }
    };

    loadAndInitializeAd();
  }, [shouldLoadAd, postId]);

  // Track visibility for analytics (only when ad is actually loaded)
  useEffect(() => {
    if (postId && isPostVisible && adReady) {
      console.log(`[MobileTopAdSenseBanner] 📊 Ad impression tracked for post ${postId} (View count: ${postViewCount})`);
      
      // ✅ ENHANCED: Track ad visibility with comprehensive content context
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'ad_impression', {
          event_category: 'mobile_adsense',
          event_label: 'top_banner_enhanced_content_aware',
          value: postId,
          custom_parameters: {
            load_strategy: contentInfo ? 'enhanced_content_aware' : 'standard',
            visibility_delay: '500ms',
            post_id: postId,
            view_count: postViewCount,
            reload_cycle: Math.floor(postViewCount / 5),
            // ✅ ENHANCED: Comprehensive content context for analytics
            has_content_info: !!contentInfo,
            content_title: contentInfo?.title?.substring(0, 50) || '',
            content_category: contentInfo?.category || 'unknown',
            content_tags_count: contentInfo?.tags?.length || 0,
            content_language: contentInfo?.language || 'vi',
            content_post_type: contentInfo?.postType || 'article',
            content_engagement: contentInfo?.engagement || 'medium',
            content_topic_category: contentInfo?.topicCategory || 'general',
            has_reading_time: !!contentInfo?.readingTime,
            optimization_enabled: true
          }
        });
      }
    }
  }, [isPostVisible, postId, adReady, contentInfo]);

  return (
    <AdContainer isVisible={true}> {/* ✅ ALWAYS VISIBLE: Fixed at top */}
      <AdWrapper>
        {reloading ? (
          /* Show transparent reloading state */
          <AdPlaceholder />
        ) : shouldLoadAd ? (
          /* Official Google AdSense Code - 320x50 standard size */
          <ins 
            className="adsbygoogle"
            style={{ 
              display: 'inline-block',
              width: '320px',
              height: '50px' /* ✅ STANDARD ADSENSE HEIGHT */
            }}
            data-ad-client="ca-pub-8980858398695883"
            data-ad-slot="4979920352"
            ref={adRef}
            key={`ad-${postId}-${Math.floor(postViewCount / 5)}`} /* ✅ UNIQUE KEY PER RELOAD CYCLE */
          />
        ) : (
          /* Loading placeholder while waiting for visibility */
          <AdPlaceholder />
        )}
      </AdWrapper>
    </AdContainer>
  );
};

export default MobileTopAdSenseBanner; 